import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { eventsAPI, uploadAPI, getShareUrl } from '../lib/api';
import ShareMenu from '../components/ShareMenu';
import MemberPicker from '../components/MemberPicker';
import MentionTextarea from '../components/MentionTextarea';
import MentionText from '../components/MentionText';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Calendar, MapPin, Clock, Users, CheckCircle, Plus, ExternalLink,
  Search, Loader2, CalendarPlus, Image as ImageIcon, Upload, X, Edit2, Share2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Skeleton } from '../components/ui/skeleton';
import { useCommunity } from '../contexts/CommunityContext';
import usePageTitle from '../hooks/usePageTitle';

const CATEGORIES = [
  { value: 'all', label: 'All Events' },
  { value: 'homecoming', label: 'Homecoming' },
  { value: 'networking', label: 'Networking' },
  { value: 'career', label: 'Career' },
  { value: 'social', label: 'Social' },
  { value: 'educational', label: 'Educational' },
];

const Events = () => {
  usePageTitle('Events');
  const { user } = useAuth();
  const { institutionFilter } = useCommunity();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '', description: '', date: '', time: '', location: '',
    category: 'social', image_url: '', ticket_price: 0, max_attendees: 100
  });
  const [eventImageFile, setEventImageFile] = useState(null);
  const [eventImagePreview, setEventImagePreview] = useState(null);
  // Optional alumni member tagged as host (distinct from the account that creates the event)
  const [memberHost, setMemberHost] = useState(null);
  // When set, the dialog is in edit mode and submit will PUT instead of POST.
  const [editingEventId, setEditingEventId] = useState(null);
  // Attendees-roster sub-dialog (lazy-loaded when opened).
  const [attendeesDialog, setAttendeesDialog] = useState(null); // { event, users, loading } or null
  const [shareEvent, setShareEvent] = useState(null);

  const openAttendeesDialog = async (ev) => {
    setAttendeesDialog({ event: ev, users: [], loading: true });
    try {
      const res = await eventsAPI.attendees(ev.event_id);
      setAttendeesDialog({ event: ev, users: Array.isArray(res.data) ? res.data : [], loading: false });
    } catch {
      setAttendeesDialog({ event: ev, users: [], loading: false });
    }
  };

  // True if the current user can edit the given event (creator OR admin).
  const isAdmin = !!(user?.role === 'admin' || user?.roles?.includes?.('admin'));
  const canEditEvent = (ev) => isAdmin || ev?.host_id === user?.user_id;

  const openEditEvent = (ev) => {
    setEditingEventId(ev.event_id);
    setNewEvent({
      title: ev.title || '',
      description: ev.description || '',
      date: ev.date || '',
      time: ev.time || '',
      location: ev.location || '',
      category: ev.category || 'social',
      image_url: ev.image_url || '',
      ticket_price: ev.ticket_price || 0,
      max_attendees: ev.max_attendees ?? 100,
      rsvp_count: ev.rsvp_count ?? 0,
    });
    setEventImagePreview(ev.image_url || null);
    setEventImageFile(null);
    setMemberHost(ev.member_host_user_id ? {
      user_id: ev.member_host_user_id,
      name: ev.member_host_name,
      profile_photo: ev.member_host_profile_photo,
    } : null);
    setSelectedEvent(null);
    setShowCreate(true);
  };

  const resetEventForm = () => {
    setNewEvent({ title: '', description: '', date: '', time: '', location: '', category: 'social', image_url: '', ticket_price: 0, max_attendees: 100 });
    removeEventImage();
    setMemberHost(null);
    setEditingEventId(null);
  };

  const handleEventImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Max 10MB');
      return;
    }
    setEventImageFile(file);
    setEventImagePreview(URL.createObjectURL(file));
  };

  const removeEventImage = () => {
    if (eventImagePreview) URL.revokeObjectURL(eventImagePreview);
    setEventImageFile(null);
    setEventImagePreview(null);
  };

  useEffect(() => {
    fetchEvents();
  }, [category, institutionFilter.institution]);

  // Auto-open the Create Event dialog when navigated to with ?create=1
  // (used by the "Post Event" button in the Feed composer).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setShowCreate(true);
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = { ...institutionFilter };
      if (category !== 'all') params.category = category;
      const res = await eventsAPI.list(params);
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (eventId) => {
    try {
      const event = events.find(e => e.event_id === eventId);
      const alreadyRSVP = (event?.attendees || []).includes(user?.user_id);
      if (alreadyRSVP) {
        await eventsAPI.cancelRsvp(eventId);
      } else {
        await eventsAPI.rsvp(eventId);
      }
      setEvents(events.map(e => {
        if (e.event_id === eventId) {
          return {
            ...e,
            rsvp_count: alreadyRSVP ? Math.max((e.rsvp_count || 1) - 1, 0) : (e.rsvp_count || 0) + 1,
            attendees: alreadyRSVP
              ? (e.attendees || []).filter(id => id !== user?.user_id)
              : [...(e.attendees || []), user?.user_id]
          };
        }
        return e;
      }));
      toast.success(alreadyRSVP ? 'RSVP cancelled' : 'RSVP confirmed!');
    } catch (error) {
      toast.error('Failed to update RSVP');
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.date) {
      toast.error('Title and date are required');
      return;
    }
    setCreating(true);
    try {
      let imageUrl = newEvent.image_url;
      if (eventImageFile) {
        const uploadRes = await uploadAPI.postImage(eventImageFile);
        imageUrl = uploadRes.data.url;
      }
      const payload = { ...newEvent, image_url: imageUrl };
      // Always send member_host_user_id (empty string clears it on update).
      payload.member_host_user_id = memberHost?.user_id || null;

      if (editingEventId) {
        const res = await eventsAPI.update(editingEventId, payload);
        setEvents(prev => prev.map(e => e.event_id === editingEventId ? (res.data || { ...e, ...payload }) : e));
        toast.success('Event updated!');
      } else {
        const res = await eventsAPI.create(payload);
        setEvents(prev => [res.data, ...prev]);
        toast.success('Event created!');
      }
      setShowCreate(false);
      resetEventForm();
    } catch (error) {
      const msg = error?.response?.data?.detail || (editingEventId ? 'Failed to update event' : 'Failed to create event');
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const addToCalendar = (event) => {
    const startDate = event.date?.replace(/-/g, '') || '';
    const title = encodeURIComponent(event.title || '');
    const location = encodeURIComponent(event.location || '');
    const details = encodeURIComponent(event.description || '');
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${startDate}&location=${location}&details=${details}`;
    window.open(url, '_blank');
  };

  const isPastEvent = (event) => {
    if (!event.date) return false;
    return new Date(event.date) < new Date(new Date().toDateString());
  };

  const filteredEvents = (searchQuery
    ? events.filter(e => e.title?.toLowerCase().includes(searchQuery.toLowerCase()) || e.location?.toLowerCase().includes(searchQuery.toLowerCase()))
    : events
  ).sort((a, b) => {
    const aPast = isPastEvent(a);
    const bPast = isPastEvent(b);
    if (aPast !== bPast) return aPast ? 1 : -1;
    return new Date(b.date || 0) - new Date(a.date || 0);
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-dark-slate">Events</h1>
            <p className="text-slate-gray mt-1">Discover and attend alumni events</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-dark-slate text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" />Create Event
          </Button>
        </div>

        {/* Search & Category Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
            <Input placeholder="Search events..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 rounded-xl bg-white border-slate-200" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map(cat => (
              <button key={cat.value} onClick={() => setCategory(cat.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  category === cat.value ? 'bg-dark-slate text-white shadow-md' : 'bg-white text-slate-gray border border-slate-200 hover:bg-slate-50'
                }`}>{cat.label}</button>
            ))}
          </div>
        </div>

        {/* Event Listings */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <Skeleton className="w-full h-40" />
                <div className="p-5 space-y-3">
                  <Skeleton className="h-4 w-20 rounded-full" />
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-10 w-full rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-ambient">
            <Calendar className="w-12 h-12 text-slate-gray mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">No events found</h3>
            <p className="text-slate-gray">Check back soon or create your own event!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEvents.map(event => {
              const isAttending = (event.attendees || []).includes(user?.user_id);
              const past = isPastEvent(event);
              return (
                <div key={event.event_id}
                  className={`bg-white rounded-2xl border border-slate-100 shadow-ambient hover:shadow-hover transition-shadow overflow-hidden cursor-pointer ${past ? 'opacity-60' : ''}`}
                  onClick={() => setSelectedEvent(event)}>
                  <div className="relative">
                    {event.image_url ? (
                      <img src={event.image_url} alt={event.title} className="w-full h-40 object-cover" />
                    ) : (
                      <div className="w-full h-40 bg-gradient-to-br from-mint-fresh to-emerald-200 flex items-center justify-center">
                        <Calendar className="w-12 h-12 text-dark-slate/30" />
                      </div>
                    )}
                    {past && (
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-slate-700 text-white text-xs">Past Event</Badge>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700 text-xs">{(event.category || 'Event').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                      {event.ticket_price > 0 && (
                        <Badge variant="secondary" className="bg-amber-50 text-amber-700 text-xs">${event.ticket_price}</Badge>
                      )}
                    </div>
                    <h3 className="font-heading font-bold text-lg text-dark-slate mb-2 line-clamp-2">{event.title}</h3>
                    {event.member_host_user_id && event.member_host_name && (
                      <Link
                        to={`/profile/${event.member_host_user_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 mb-2 text-xs text-slate-gray hover:text-dark-slate transition-colors"
                      >
                        <span>Hosted by</span>
                        {event.member_host_profile_photo && (
                          <img src={event.member_host_profile_photo} alt="" className="w-4 h-4 rounded-full object-cover" />
                        )}
                        <span className="font-medium underline">{event.member_host_name}</span>
                      </Link>
                    )}
                    <div className="space-y-1.5 text-sm text-slate-gray mb-3">
                      {event.date && <p className="flex items-center"><Clock className="w-4 h-4 mr-2" />{event.date} {event.time && `at ${event.time}`}</p>}
                      {event.location && <p className="flex items-center"><MapPin className="w-4 h-4 mr-2" />{event.location}</p>}
                      <p className="flex items-center"><Users className="w-4 h-4 mr-2" />{event.rsvp_count || 0} attending</p>
                    </div>
                    <Button onClick={(e) => { e.stopPropagation(); handleRSVP(event.event_id); }}
                      variant={isAttending ? 'secondary' : 'default'}
                      className={`w-full rounded-full ${isAttending ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-dark-slate text-white hover:bg-slate-800'}`}>
                      {isAttending ? <><CheckCircle className="w-4 h-4 mr-2" />Attending</> : 'RSVP'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Event Detail Modal */}
        <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
            {selectedEvent && (
              <>
                {selectedEvent.image_url && (
                  <img src={selectedEvent.image_url} alt={selectedEvent.title} className="w-full h-48 object-cover rounded-xl -mt-2 mb-4" />
                )}
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-dark-slate">{selectedEvent.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-3">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="bg-blue-50 text-blue-700">{selectedEvent.category || 'Event'}</Badge>
                    {selectedEvent.ticket_price > 0 && <Badge variant="secondary" className="bg-amber-50 text-amber-700">${selectedEvent.ticket_price}</Badge>}
                  </div>
                  <div className="space-y-2 text-sm text-slate-gray">
                    {selectedEvent.date && <p className="flex items-center"><Calendar className="w-4 h-4 mr-2" />{selectedEvent.date} {selectedEvent.time && `at ${selectedEvent.time}`}</p>}
                    {selectedEvent.location && <p className="flex items-center"><MapPin className="w-4 h-4 mr-2" />{selectedEvent.location}</p>}
                    <button
                      onClick={() => openAttendeesDialog(selectedEvent)}
                      disabled={(selectedEvent.rsvp_count || 0) === 0}
                      className="flex items-center text-left enabled:hover:underline enabled:hover:text-dark-slate transition-colors disabled:cursor-default"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      {selectedEvent.rsvp_count || 0} attending{selectedEvent.max_attendees ? ` / ${selectedEvent.max_attendees} max` : ''}
                    </button>
                  </div>
                  {selectedEvent.member_host_user_id && selectedEvent.member_host_name ? (
                    <Link
                      to={`/profile/${selectedEvent.member_host_user_id}`}
                      className="flex items-center gap-3 bg-mint-fresh/20 rounded-xl p-3 text-sm hover:bg-mint-fresh/30 transition-colors"
                    >
                      {selectedEvent.member_host_profile_photo && (
                        <img src={selectedEvent.member_host_profile_photo} alt="" className="w-9 h-9 rounded-full object-cover" />
                      )}
                      <div className="flex-1">
                        <p className="text-xs text-slate-gray">Hosted by</p>
                        <p className="font-semibold text-dark-slate">{selectedEvent.member_host_name}</p>
                      </div>
                    </Link>
                  ) : selectedEvent.host_name ? (
                    <div className="bg-mint-fresh/20 rounded-xl p-3 text-sm">
                      Hosted by <span className="font-semibold text-dark-slate">{selectedEvent.host_name}</span>
                    </div>
                  ) : null}
                  {selectedEvent.description && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-1">About this event</h4>
                      <p className="text-slate-gray text-sm"><MentionText text={selectedEvent.description} /></p>
                    </div>
                  )}
                  <div className="flex gap-3 pt-4 border-t border-slate-100 flex-wrap">
                    <Button className="flex-1 h-11 bg-dark-slate text-white rounded-full" onClick={() => handleRSVP(selectedEvent.event_id)}>
                      {(selectedEvent.attendees || []).includes(user?.user_id) ? <><CheckCircle className="w-4 h-4 mr-2" />Attending</> : 'RSVP'}
                    </Button>
                    <Button variant="outline" className="h-11 rounded-full px-4" onClick={() => addToCalendar(selectedEvent)}>
                      <CalendarPlus className="w-4 h-4 mr-2" />Add to Calendar
                    </Button>
                    <Button variant="outline" className="h-11 rounded-full px-4" onClick={() => setShareEvent(selectedEvent)}>
                      <Share2 className="w-4 h-4 mr-2" />Share
                    </Button>
                    {canEditEvent(selectedEvent) && (
                      <Button variant="outline" className="h-11 rounded-full px-4" onClick={() => openEditEvent(selectedEvent)}>
                        <Edit2 className="w-4 h-4 mr-2" />Edit
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Create / Edit Event Dialog — same form, mode driven by editingEventId */}
        <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) resetEventForm(); }}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEventId ? 'Edit Event' : 'Create Event'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label>Event Title *</Label><Input value={newEvent.title} onChange={(e) => setNewEvent(p => ({ ...p, title: e.target.value }))} placeholder="Event name" className="mt-1" /></div>
              <div>
                <Label>Description</Label>
                <div className="mt-1">
                  <MentionTextarea
                    value={newEvent.description}
                    onChange={(v) => setNewEvent(p => ({ ...p, description: v }))}
                    placeholder="Describe your event... type @ to mention an alum."
                    rows={3}
                    className="resize-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date *</Label><Input type="date" value={newEvent.date} onChange={(e) => setNewEvent(p => ({ ...p, date: e.target.value }))} className="mt-1" /></div>
                <div><Label>Time</Label><Input type="time" value={newEvent.time} onChange={(e) => setNewEvent(p => ({ ...p, time: e.target.value }))} className="mt-1" /></div>
              </div>
              <div><Label>Location</Label><Input value={newEvent.location} onChange={(e) => setNewEvent(p => ({ ...p, location: e.target.value }))} placeholder="Event location" className="mt-1" /></div>
              <div>
                <Label>Member Host (optional)</Label>
                <p className="text-[11px] text-slate-gray mb-1.5 mt-0.5">Tag the alumni member who's hosting. They'll be featured on the event listing.</p>
                <MemberPicker value={memberHost} onChange={setMemberHost} placeholder="Search alumni by name..." />
              </div>
              <div><Label>Category</Label>
                <Select value={newEvent.category} onValueChange={(v) => setNewEvent(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.filter(c => c.value !== 'all').map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Event Cover Photo (optional)</Label>
                <div className="mt-1">
                  {eventImagePreview ? (
                    <div className="relative">
                      <img
                        src={eventImagePreview}
                        alt="Event preview"
                        className="w-full h-40 object-cover rounded-xl border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={removeEventImage}
                        className="absolute top-2 right-2 bg-dark-slate text-white rounded-full p-1.5 hover:bg-red-500 transition-colors shadow"
                        aria-label="Remove image"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors">
                      <Upload className="w-6 h-6 text-slate-400 mb-1" />
                      <span className="text-sm font-medium text-slate-gray">Upload a photo</span>
                      <span className="text-[11px] text-slate-gray mt-0.5">PNG, JPG up to 10MB</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleEventImageSelect} />
                    </label>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ticket Price (USD)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newEvent.ticket_price}
                    onChange={(e) => setNewEvent(p => ({ ...p, ticket_price: e.target.value === '' ? 0 : Number(e.target.value) }))}
                    placeholder="0 for free"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Max Attendees</Label>
                  <Input
                    type="number"
                    min="0"
                    value={newEvent.max_attendees}
                    onChange={(e) => setNewEvent(p => ({ ...p, max_attendees: e.target.value === '' ? null : Number(e.target.value) }))}
                    placeholder="e.g. 100"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Admin-only: manual RSVP count override (e.g. to clear out a stale seeded count). */}
              {editingEventId && isAdmin && (
                <div>
                  <Label>RSVP Count (admin)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={newEvent.rsvp_count ?? ''}
                    onChange={(e) => setNewEvent(p => ({ ...p, rsvp_count: e.target.value === '' ? null : Number(e.target.value) }))}
                    placeholder="Leave blank to keep current count"
                    className="mt-1"
                  />
                  <p className="text-[11px] text-slate-gray mt-1">Real RSVPs from members still increment from this value.</p>
                </div>
              )}

              <Button onClick={handleCreateEvent} disabled={creating} className="w-full h-11 bg-dark-slate text-white rounded-full">
                {creating
                  ? (editingEventId ? 'Saving...' : 'Creating...')
                  : (editingEventId ? 'Save Changes' : 'Create Event')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Attendees roster — opened by tapping the RSVP count on event detail */}
        <Dialog open={!!attendeesDialog} onOpenChange={(open) => !open && setAttendeesDialog(null)}>
          <DialogContent className="max-w-sm rounded-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {attendeesDialog?.event?.title} · {attendeesDialog?.users?.length || 0} attending
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-1 mt-2">
              {attendeesDialog?.loading && (
                <div className="flex items-center gap-2 px-2 py-3 text-sm text-slate-gray">
                  <Loader2 className="w-4 h-4 animate-spin" />Loading attendees...
                </div>
              )}
              {!attendeesDialog?.loading && (attendeesDialog?.users || []).map(u => (
                <Link
                  key={u.user_id}
                  to={`/profile/${u.user_id}`}
                  onClick={() => setAttendeesDialog(null)}
                  className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-slate-50"
                >
                  <Avatar className="w-9 h-9">
                    <AvatarImage src={u.profile_photo} />
                    <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{u.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-dark-slate truncate">{u.name}</p>
                    {(u.title || u.institution) && (
                      <p className="text-xs text-slate-gray truncate">{[u.title, u.institution].filter(Boolean).join(' · ')}</p>
                    )}
                  </div>
                </Link>
              ))}
              {!attendeesDialog?.loading && (attendeesDialog?.users || []).length === 0 && (
                <p className="text-sm text-slate-gray text-center py-6">No one has RSVP'd yet.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Share event dialog */}
        <Dialog open={!!shareEvent} onOpenChange={(open) => !open && setShareEvent(null)}>
          <DialogContent className="max-w-sm rounded-2xl">
            <DialogHeader>
              <DialogTitle>Share this event</DialogTitle>
            </DialogHeader>
            {shareEvent && (
              <div className="space-y-4 mt-2">
                <div className="bg-slate-50 rounded-xl p-3 flex items-start gap-3">
                  {shareEvent.image_url && (
                    <img src={shareEvent.image_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-dark-slate text-sm line-clamp-1">{shareEvent.title}</p>
                    {shareEvent.date && <p className="text-xs text-slate-gray">{shareEvent.date}{shareEvent.time ? ` at ${shareEvent.time}` : ''}</p>}
                    {shareEvent.location && <p className="text-xs text-slate-gray line-clamp-1">{shareEvent.location}</p>}
                  </div>
                </div>
                <ShareMenu url={getShareUrl('event', shareEvent.event_id)} title={shareEvent.title} />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Events;
